import { Inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  catchError,
  concatMap,
  EMPTY,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { ModalService } from '@cp/ui/modal/modal.service';
import {
  ErrorModalComponent,
  ErrorModalData,
} from '@cp/ui/modal/modals/error-modal/error-modal.component';
import { Environment } from '@cp/shared/environment/environment.interface';
import { SHARED_ENVIRONMENT } from '@cp/shared/environment/environment.token';
import { SharedHttpService } from '../shared-http.service';
import { ApiError, ErrorResponse, ResponseErrors } from '../shared-http.models';

@Injectable()
export class ErrorInterceptor
  extends SharedHttpService
  implements HttpInterceptor
{
  constructor(
    private _modalService: ModalService,
    private _authService: AuthService,
    @Inject(SHARED_ENVIRONMENT) private _environment: Environment,
  ) {
    super();
  }

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.error) {
          // For download, we use responseType: 'blob'.
          // If there's an error, Angular returns the error as the same type.
          // https://github.com/angular/angular/issues/19148
          // Workaround: https://github.com/angular/angular/issues/19148#issuecomment-1029327767
          let responseError: ResponseErrors = error.error;
          if (request.responseType === 'blob' && error.error instanceof Blob) {
            responseError = this._blobToResponseErrors(error.error);
          }

          switch (error.status) {
            case 401:
              return this._handleErrorStatus401(responseError);
            case 0:
              return this._handleErrorStatus0(error);
            default:
              return this._defaultHandleHttpError(responseError, error.status);
          }
        }

        console.error('unknown error', error);

        return throwError(() => error);
      }),
    );
  }

  private _handleErrorStatus401(
    responseError: ResponseErrors,
  ): Observable<never> {
    if (
      responseError.errors.some((error) => error.code === 'staff_not_current')
    ) {
      const ref = this._modalService.openUniversalErrorModal();
      return ref.afterClosed().pipe(
        tap(() =>
          this._authService.logout({
            logoutParams: {
              returnTo: this._environment.auth0.logoutUrl,
            },
          }),
        ),
        concatMap(() => EMPTY),
      );
    }

    if (
      responseError.errors.some(
        (error) => error.code === 'required_claim_not_found',
      )
    ) {
      const ref = this._modalService.openUniversalErrorModal();
      return ref.afterClosed().pipe(
        tap(() =>
          this._authService.logout({
            logoutParams: {
              returnTo: this._environment.auth0.logoutUrl,
            },
          }),
        ),
        concatMap(() => EMPTY),
      );
    }

    if (
      responseError.errors.some(
        (error) => error.code === 'staff_in_incorrect_call_centre_status',
      )
    ) {
      const ref = this._modalService.openErrorIncorrectStatusModal();
      return ref.afterClosed().pipe(
        tap(() =>
          this._authService.logout({
            logoutParams: {
              returnTo: this._environment.auth0.logoutUrl,
            },
          }),
        ),
        concatMap(() => EMPTY),
      );
    }

    if (
      responseError.errors.some(
        (error) => error.code === 'user_profile_not_found',
      )
    ) {
      const ref = this._modalService.openUniversalErrorModal();
      return ref.afterClosed().pipe(
        tap(() =>
          this._authService.logout({
            logoutParams: {
              returnTo: this._environment.auth0.logoutUrl,
            },
          }),
        ),
        concatMap(() => EMPTY),
      );
    }

    const hasError = !!responseError?.errors;
    const logoutMessage = `Compliance Portal is not fully authenticated. Therefore, you will be redirected to the login page.${
      hasError ? ' The below will provide more information as to why.' : ''
    }`;
    const modalRef = this._modalService.openModalFromComponent<
      ErrorModalComponent,
      ErrorModalData
    >(ErrorModalComponent, {
      title: 'Something went wrong!',
      errors: hasError
        ? [logoutMessage, ...this.mapErrors(responseError.errors)]
        : [logoutMessage],
    });

    return modalRef.afterClosed().pipe(
      tap(() =>
        this._authService.logout({
          logoutParams: {
            returnTo: this._environment.auth0.logoutUrl,
          },
        }),
      ),
      // The above will redirect the user anyway, this line is needed
      // to satisfy TS about returning type Observable<never>
      concatMap(() => EMPTY),
    );
  }

  private _handleErrorStatus0(error: HttpErrorResponse): Observable<never> {
    // A client-side or network error occurred. Handle it accordingly.
    console.error('An error occurred:', error.error);
    return throwError(() => error);
  }

  private _defaultHandleHttpError(
    errorObject: ResponseErrors,
    status: number,
  ): Observable<never> {
    const returnObject: ErrorResponse = {
      errors: errorObject?.errors,
      status,
    };
    // The backend returned an unsuccessful response code.
    // The response body may contain clues as to what went wrong.
    console.error(
      `Backend returned code ${status}, body was: ${returnObject.errors?.map(
        (e: ApiError) => e.message,
      )}`,
    );
    return throwError(() => returnObject);
  }

  private _blobToResponseErrors(blob: Blob): ResponseErrors {
    const url = URL.createObjectURL(blob);
    const xmlRequest = new XMLHttpRequest();
    xmlRequest.open('GET', url, false);
    xmlRequest.send();
    URL.revokeObjectURL(url);
    return JSON.parse(xmlRequest.responseText);
  }
}
